import {
    Chip,
    Stack,
    Tooltip,
    Typography,
    Avatar,
    AvatarGroup,
    alpha,
    SvgIcon,
    Card,
    CardContent,
    Button,
    Box,
    Popper,
    Paper,
    ClickAwayListener,
    debounce,
    Skeleton,
} from "@mui/material";
import {
    DataGridPro,
    GridColDef,
    GridColumnGroupingModel,
    GridColumnHeaders,
    GridPaginationModel,
    GridRenderCellParams,
    GridColumnVisibilityModel,
    GridFilterModel,
    GridLogicOperator,
    GridToolbar,
    useGridApiContext,
    useGridRootProps,
} from "@mui/x-data-grid-pro";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSearchTermsGridQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings/selectors";
import { RootState } from "src/context/store";
import { fDate } from "src/utils/format-time";
import { fPercent, fNumber } from "src/utils/format-number";
import { formatCurrency } from "src/utils/format-currency";
import Iconify from "src/components/iconify";
import ColumnStackVertical from "./column-stack-vertical";
import Chart from "src/components/chart";
import CursorClickIcon from "./icons/cursor-click-icon";
import TextIcon from "./icons/text-icon";
import ShoppingCartIcon from "./icons/shopping-cart";
import { useResponsive } from "src/hooks/use-responsive";
import CustomPopover, { usePopover } from "src/components/custom-popover";
import CompetitorsTooltip from "./tooltips/competitors";
import PaidSearchShareTooltip from "./tooltips/paid-search-share";
import ClicksSplitTooltip from "./tooltips/clicks-split";
import PaidShoppingShareTooltip from "./tooltips/paid-shopping-share";
import { CategoryButton } from "./category-button";
import ExportIcon from "./icons/export";
import { FilterPanelCustom } from "src/components/data-grid/filter-panel-custom";
import { styled } from "@mui/material/styles";
import { useLocales } from "src/locales";


const GridPanelRoot = styled(Popper, {
    name: 'MuiDataGrid',
    slot: 'Panel',
    overridesResolver: (props, styles) => styles.panel
})(({
    theme
}) => ({
    zIndex: (theme as any).zIndex.modal
}));
const GridPaperRoot = styled(Paper, {
    name: 'MuiDataGrid',
    slot: 'Paper',
    overridesResolver: (props, styles) => styles.paper
})(({
    theme
}) => ({
    backgroundColor: ((theme as any).vars || theme).palette.background.paper,
    minWidth: 300,
    maxHeight: 450,
    display: 'flex',
    maxWidth: `calc(100vw - ${theme.spacing(0.5)})`,
    overflow: 'auto'
}));

const colorRange = [
    {
        min: 0,
        max: 30,
        color: "#F5CE47",
    },
    {
        min: 30,
        max: 70,
        color: "#BC9510",
    },
    {
        min: 70,
        max: 100,
        color: "#18A155",
    },
]

const opportunityColors = {
    "no opportunity": { backgroundColor: alpha("#637381", 0.18), color: "#637381" },
    "overlap": { backgroundColor: alpha("#23F07F", 0.18), color: "#20D873" },
    "seo boost": { backgroundColor: alpha("#FAB000", 0.3), color: "#DBC272" },
    "sea boost": { backgroundColor: alpha("#A46FF7", 0.19), color: "#CEA8F0" },
}


const useSearchTermsGridData = (paginationModel: GridPaginationModel & GridFilterModel) => {
    const workspace = useCurrentWorkspace();
    const start = useSelector((state: RootState) => state.filters.start);
    const end = useSelector((state: RootState) => state.filters.end);
    const devices = useSelector((state: RootState) => state.filters.devices);
    const countries = useSelector((state: RootState) => state.filters.countries);
    const platforms = useSelector((state: RootState) => state.filters.platforms);
    const searchTerms = useSelector((state: RootState) => state.filters.keywords);
    const {
        data: searchTermsGrid,
        isLoading,
        isError,
        isFetching,
    } = useSearchTermsGridQuery({
        workspace_id: workspace?.id as number,
        start_date: fDate(start, "yyyy-MM-dd"),
        end_date: fDate(end, "yyyy-MM-dd"),
        devices: devices,
        countries: countries,
        platforms: platforms,
        limit: paginationModel.pageSize,
        page: paginationModel.page,
        filterModel: paginationModel.items,
    });

    return {
        searchTermsGrid,
        isLoading,
        isError,
        isFetching,
    };
};

const useColumns = () => {
    const { currentLang: { value: locale } } = useLocales();
    const workspace = useCurrentWorkspace();
    const valueOrEmpty = (value: any, format: (value: any) => string = String) => !!value ? format(value) : "-";
    const formatToCurrency = (value: any) => formatCurrency(value, workspace?.currency, locale, true);
    const formatPercent = (value: any) => value > 1 ? value.toFixed(0) + "%" : value + "%";
    const [popperRefs, setPopperRefs] = useState<any>(null);
    return [
        {
            field: "search_term",
            headerName: "Search Term",
            type: "string",
            renderHeader(params) {
                return (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: "100%" }}>
                        <SvgIcon component={CursorClickIcon} />
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                            {params.colDef.headerName}
                        </Typography>
                    </Stack>
                );
            },
            flex: 1,
            minWidth: 175,
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title={params.row.search_term}>

                        <Typography variant="body2" textOverflow="ellipsis" overflow="hidden" noWrap sx={{ fontWeight: 600 }}>
                            {params.row.search_term}
                        </Typography>
                    </Tooltip>
                    <Stack flexGrow={1} />
                    <Typography variant="body2" color="text.secondary">
                        {fNumber(params.row.total_clicks)}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: "clicks_split",
            headerName: "Clicks split",
            cellClassName: "column--data-column",
            sortable: false,
            disableColumnMenu: true,
            flex: 1,
            filterable: false,
            minWidth: 225,
            renderCell: (params: GridRenderCellParams) => {
                const rowId = params.row.id;

                const popover = usePopover();
                const containerRef = useRef<HTMLDivElement>(null);

                const onPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
                    popover.onOpen(event);
                    setPopperRefs(rowId);
                };

                useEffect(() => {
                    if (popperRefs !== rowId) {
                        popover.onClose();
                    }
                }, [popperRefs]);

                const onPopoverClose = () => {
                    popover.onClose();
                    setPopperRefs(null);
                };

                return (
                    <>
                        <Stack
                            ref={containerRef}
                            onMouseEnter={onPopoverOpen}
                            onMouseLeave={onPopoverClose}
                            aria-owns={popover.open ? "clicks-split-popover" : undefined}
                            aria-haspopup="true"
                            direction="row"
                            alignItems="center"
                            justifyContent="left"
                            spacing={1}
                            sx={{ width: "100%", height: "50px", minHeight: "50px" }}
                        >
                            <ColumnStackVertical series={[
                                {
                                    name: "Ad Clicks",
                                    data: [params.row.percent_ad_clicks_from_search],
                                    color: "#18A155",
                                },
                                {
                                    name: "Shopping Clicks",
                                    data: [params.row.percent_ad_clicks_from_shopping],
                                    color: "#BC9510",
                                },
                                {
                                    name: "Organic Clicks",
                                    data: [params.row.percent_organic_clicks],
                                    color: "#F5CE47",
                                },
                            ]} />
                        </Stack>
                        <CustomPopover
                            open={popover.open}
                            onClose={popover.onClose}
                            anchorEl={containerRef.current}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "center",
                            }}
                            slotProps={{
                                root: {
                                    sx: {
                                        pointerEvents: "none",
                                    },
                                },
                            }}
                            disableRestoreFocus
                        >
                            <ClicksSplitTooltip
                                onMouseLeave={popover.onClose}
                                percentAdClicksFromSearch={params.row.percent_ad_clicks_from_search}
                                percentAdClicksFromShopping={params.row.percent_ad_clicks_from_shopping}
                                percentOrganicClicks={params.row.percent_organic_clicks}
                            />
                        </CustomPopover>
                    </>
                );
            },
        },
        {
            field: "visibility_rate",
            headerName: "Visibility rate",
            flex: 1,
            minWidth: 150,
            type: "number",
            renderCell: (params: GridRenderCellParams) => {
                const color = colorRange.find(c => params.row.visibility_rate >= c.min && params.row.visibility_rate <= c.max)?.color || "success.main";
                return (
                    <Stack direction="row" alignItems="center" spacing={1} py={1} sx={{ width: "100%", height: "50px" }}>
                        <Typography variant="body2">{valueOrEmpty(params.row.visibility_rate, formatPercent)}</Typography>
                        <Chart type="bar" series={[
                            {
                                name: "Visibility Rate",
                                data: [params.row.visibility_rate],
                                color,
                            },
                        ]} options={{
                            chart: {
                                width: "125%",
                                height: "20px",
                                toolbar: {
                                    show: false,
                                },
                                offsetX: -15,
                            },
                            plotOptions: {
                                bar: {
                                    barHeight: '100%',
                                    borderRadius: 3,
                                    borderRadiusApplication: 'around',
                                    borderRadiusWhenStacked: 'last',
                                    horizontal: true,
                                },
                            },
                            xaxis: {
                                min: 0,
                                max: 100,
                                labels: {
                                    show: false,
                                },
                                axisBorder: {
                                    show: false,
                                },
                                axisTicks: {
                                    show: false,
                                },
                            },
                            grid: {
                                show: false,
                                xaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                yaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                            },
                            dataLabels: {
                                enabled: false,
                            },
                            legend: {
                                show: false,
                            },
                            tooltip: {
                                enabled: false,
                            },
                            yaxis: {
                                min: 0,
                                max: 100,
                                show: false,
                                axisBorder: {
                                    show: false,
                                },
                                axisTicks: {
                                    show: false,
                                }
                            },
                            colors: [color],
                        }} />
                    </Stack>
                )
            },
        },
        {
            field: "avg_seo_position",
            headerName: "Avg. SEO Position",
            flex: 1,
            minWidth: 95,
            align: "left",
            type: "number",
            renderCell: (params: GridRenderCellParams) => {
                const position = valueOrEmpty(params.row.avg_seo_position, (value: any) => value?.toFixed(1) || "0");
                const change = params.row.seo_position_change;
                const changeText = () => change > 0 ? `+${change}` : `${change}`;

                return (
                    <Stack direction="row" alignItems="center" px={1} py={1} spacing={1}>
                        <Chip
                            label={position}
                            size="small"
                            sx={{
                                width: 35,
                                height: 35,
                                borderRadius: 1.5,
                                fontWeight: 600,
                                fontSize: 12,
                                backgroundColor: (theme) => alpha("#9C7C9C", 0.3),
                                color: (theme) => "white",
                            }}
                        />
                        <Typography
                            variant="body2"
                            color={change > 0 ? "success.main" : change < 0 ? "error.main" : "text.secondary"}
                            sx={{ fontWeight: 600 }}
                        >
                            {valueOrEmpty(change, changeText)}
                        </Typography>
                    </Stack>
                );
            },
        },
        {
            field: "paid_search_visibility_rate",
            headerName: "Paid Search Share",
            cellClassName: "column--highlighblue",
            headerClassName: "column--highlighblue",
            flex: 1,
            minWidth: 170,
            type: "number",
            renderCell: (params: GridRenderCellParams) => {
                const popover = usePopover();
                const containerRef = useRef<HTMLDivElement>(null);
                const popoverRef = useRef<HTMLDivElement>(null);
                const rowId = params.row.id;

                const onPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
                    popover.onOpen(event);
                    setPopperRefs(rowId);
                };

                const onPopoverClose = () => {
                    popover.onClose();
                    setPopperRefs(null);
                };

                useEffect(() => {
                    if (popperRefs !== rowId) {
                        popover.onClose();
                    }
                }, [popperRefs]);

                const value = params.row.paid_search_visibility_rate || 0;

                if (!value) {
                    return (
                        <Stack
                            ref={containerRef}
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="left"
                            spacing={1}
                            sx={{ width: "100%", minHeight: "50px" }}
                        >
                            <Typography variant="body2">-</Typography>
                        </Stack>
                    );
                }

                // Get breakdown values for the 3 segments
                const firstSearchRate = params.row.first_search_visibility_rate;
                const topSearchRate = params.row.top_search_visibility_rate;
                const competSearchRate = params.row.compet_search_visibility_rate;

                // Calculate percentages for each segment
                const firstPercent = Math.min(Math.max(firstSearchRate, 0), 100);
                const topPercent = Math.min(Math.max(topSearchRate, 0), 100);
                const competPercent = Math.min(Math.max(competSearchRate, 0), 100);

                // Calculate total for proportional display
                const total = firstPercent + topPercent + competPercent;
                const firstBarWidth = total > 0 ? (firstPercent / total) * 100 : 0;
                const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
                const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;
                return (
                    <>
                        <Stack
                            ref={containerRef}
                            onMouseEnter={!!value ? onPopoverOpen : undefined}
                            onMouseLeave={!!value ? onPopoverClose : undefined}
                            aria-owns={popover.open ? "paid-search-share-popover" : undefined}
                            aria-haspopup="true"
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1.5}
                            sx={{ width: "100%", minHeight: "50px" }}
                        >
                            <Typography variant="body2" sx={{ minWidth: 40, color: "white" }}>
                                {valueOrEmpty(value, (value: any) => value?.toFixed(0) + "%")}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexGrow={1} sx={{ height: 12 }}>
                                {/* 1-3 position segment (bright yellow) */}
                                {firstBarWidth > 0 && (
                                    <Stack
                                        sx={{
                                            height: 12,
                                            flex: firstBarWidth,
                                            backgroundColor: "#F8D83A",
                                            borderRadius: "2px",
                                            minWidth: firstBarWidth > 0 ? "2px" : 0,
                                        }}
                                    />
                                )}
                                {/* 4-10 position segment (lighter yellow) */}
                                {topBarWidth > 0 && (
                                    <Stack
                                        sx={{
                                            height: 12,
                                            flex: topBarWidth,
                                            backgroundColor: "#FFFAAE",
                                            borderRadius: "2px",
                                            minWidth: topBarWidth > 0 ? "2px" : 0,
                                        }}
                                    />
                                )}
                                {/* Competitors segment (grey) */}
                                {competBarWidth > 0 && (
                                    <Stack
                                        sx={{
                                            height: 12,
                                            flex: competBarWidth,
                                            backgroundColor: "#637381",
                                            borderRadius: "2px",
                                            minWidth: competBarWidth > 0 ? "2px" : 0,
                                        }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                        <CustomPopover
                            id="paid-search-share-popover"
                            open={popover.open}
                            onClose={onPopoverClose}
                            arrow="bottom-center"
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "center",
                            }}
                            slotProps={{
                                root: {
                                    sx: {
                                        pointerEvents: "none",
                                    },
                                },
                            }}
                            disableRestoreFocus
                        >
                            <div
                                ref={popoverRef}
                            >
                                <PaidSearchShareTooltip
                                    firstSearchVisibilityRate={params.row.first_search_visibility_rate}
                                    topSearchVisibilityRate={params.row.top_search_visibility_rate}
                                    competSearchVisibilityRate={params.row.compet_search_visibility_rate}
                                />
                            </div>
                        </CustomPopover>
                    </>
                );
            },
        },
        {
            field: "paid_shopping_visibility_rate",
            headerName: "Paid Shopping Share",
            cellClassName: "column--highlighpurple",
            headerClassName: "column--highlighpurple",
            flex: 1,
            minWidth: 170,
            type: "number",
            renderCell: (params: GridRenderCellParams) => {
                const popover = usePopover();
                const containerRef = useRef<HTMLDivElement>(null);
                const rowId = params.row.id;

                const onPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
                    popover.onOpen(event);
                    setPopperRefs(rowId);
                };

                const onPopoverClose = () => {
                    popover.onClose();
                    setPopperRefs(null);
                };

                useEffect(() => {
                    if (popperRefs !== rowId) {
                        popover.onClose();
                    }
                }, [popperRefs]);

                const value = params.row.paid_shopping_visibility_rate || 0;



                if (!value) {
                    return (
                        <Stack
                            ref={containerRef}
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="left"
                            spacing={1}
                            sx={{ width: "100%", minHeight: "50px" }}
                        >
                            <Typography variant="body2">-</Typography>
                        </Stack>
                    );
                }

                // Get breakdown values for the 3 segments
                const lastShoppingRate = params.row.last_shopping_visibility_rate;
                const topShoppingRate = params.row.top_shopping_visibility_rate;
                const competShoppingRate = params.row.compet_shopping_visibility_rate;

                // Calculate percentages for each segment
                const lastPercent = Math.min(Math.max(lastShoppingRate, 0), 100);
                const topPercent = Math.min(Math.max(topShoppingRate, 0), 100);
                const competPercent = Math.min(Math.max(competShoppingRate, 0), 100);

                // Calculate total for proportional display
                const total = lastPercent + topPercent + competPercent;
                const lastBarWidth = total > 0 ? (lastPercent / total) * 100 : 0;
                const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
                const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;

                return (
                    <>
                        <Stack
                            ref={containerRef}
                            onMouseEnter={!!value ? onPopoverOpen : undefined}
                            onMouseLeave={!!value ? onPopoverClose : undefined}
                            aria-owns={popover.open ? "paid-shopping-share-popover" : undefined}
                            aria-haspopup="true"
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1.5}
                            sx={{ width: "100%", minHeight: "50px" }}
                        >
                            <Typography variant="body2" sx={{ minWidth: 40, color: "white" }}>
                                {valueOrEmpty(value, formatPercent)}
                            </Typography>
                            <Stack direction="row" spacing={0.5} flexGrow={1} sx={{ height: 12 }}>
                                {/* 1st position segment (orange) */}
                                {lastBarWidth > 0 && (
                                    <Stack
                                        sx={{
                                            height: 12,
                                            flex: lastBarWidth,
                                            backgroundColor: "#FF9A0B",
                                            borderRadius: "2px",
                                            minWidth: lastBarWidth > 0 ? "2px" : 0,
                                        }}
                                    />
                                )}
                                {/* 2-3 position segment (lighter orange) */}
                                {topBarWidth > 0 && (
                                    <Stack
                                        sx={{
                                            height: 12,
                                            flex: topBarWidth,
                                            backgroundColor: "#FFD89F",
                                            borderRadius: "2px",
                                            minWidth: topBarWidth > 0 ? "2px" : 0,
                                        }}
                                    />
                                )}
                                {/* Competitors segment (grey) */}
                                {competBarWidth > 0 && (
                                    <Stack
                                        sx={{
                                            height: 12,
                                            flex: competBarWidth,
                                            backgroundColor: "#637381",
                                            borderRadius: "2px",
                                            minWidth: competBarWidth > 0 ? "2px" : 0,
                                        }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                        <CustomPopover
                            id="paid-shopping-share-popover"
                            open={popover.open}
                            onClose={onPopoverClose}
                            arrow="bottom-center"
                            anchorEl={popover.open}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "center",
                            }}
                            slotProps={{
                                root: {
                                    sx: {
                                        pointerEvents: "none",
                                    },
                                },
                            }}
                            disableRestoreFocus
                        >
                            <PaidShoppingShareTooltip
                                lastShoppingVisibilityRate={params.row.last_shopping_visibility_rate}
                                topShoppingVisibilityRate={params.row.top_shopping_visibility_rate}
                                competShoppingVisibilityRate={params.row.compet_shopping_visibility_rate}
                            />
                        </CustomPopover>
                    </>
                );
            },
        },
        {
            field: "top_comp",
            headerName: "Top Comp.",
            flex: 1,
            sortable: false,
            disableColumnMenu: true,
            minWidth: 120,
            renderHeader: (params: GridRenderCellParams) => (
                <Stack direction="row" alignItems="center" justifyContent="space-around" spacing={1}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                        {params.colDef.headerName}
                    </Typography>
                </Stack>
            ),
            renderCell: (params: GridRenderCellParams) => {
                const popover = usePopover();
                const containerRef = useRef<HTMLDivElement>(null);
                const rowId = params.row.id;

                const onPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
                    popover.onOpen(event);
                    setPopperRefs(rowId);
                };

                const onPopoverClose = () => {
                    popover.onClose();
                    setPopperRefs(null);
                };

                useEffect(() => {
                    if (popperRefs !== rowId) {
                        popover.onClose();
                    }
                }, [popperRefs]);

                if (!params.row.top_compet__process?.length) {
                    return (
                        <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="left"
                            sx={{ width: "100%", minHeight: "50px" }}
                        >
                            <Typography variant="body2">-</Typography>
                        </Stack>
                    );
                }

                return (
                    <>
                        <Stack
                            ref={containerRef}
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            spacing={1}
                            sx={{ width: "100%", minHeight: "50px" }}
                        >
                            <AvatarGroup
                                max={4}
                                aria-owns={popover.open ? "competitors-popover" : undefined}
                                aria-haspopup="true"
                                onMouseEnter={onPopoverOpen}
                                onMouseLeave={onPopoverClose}
                                sx={{
                                    '& .MuiAvatar-root': {
                                        width: 24,
                                        height: 24,
                                        bgcolor: (theme) => theme.palette.primary.main,
                                        color: (theme) => theme.palette.primary.contrastText
                                    },
                                }}
                            >
                                {([...(params.row.top_compet__process || [])])?.sort((a: any, b: any) => b.total_rate - a.total_rate).map((c: any, i: number) => (
                                    <Avatar key={i} alt={c.domain_name} src={`https://www.google.com/s2/favicons?domain=${c.domain_name}&sz=32`} />
                                ))}
                            </AvatarGroup>
                        </Stack>
                        <CustomPopover
                            id="competitors-popover"
                            open={popover.open}
                            onClose={onPopoverClose}
                            arrow="bottom-center"
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "center",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "center",
                            }}
                            slotProps={{
                                root: {
                                    sx: {
                                        pointerEvents: "none",
                                    },
                                },
                            }}
                            disableRestoreFocus
                        >
                            <CompetitorsTooltip competitors={params.row.top_compet__process} />
                        </CustomPopover>
                    </>
                )
            },
        },
        {
            field: "ad_cost",
            headerName: "Ad Cost",
            flex: 1,
            minWidth: 75,
            type: "number",
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" alignItems="flex-start" justifyContent="left" spacing={0.5} py={1} sx={{ width: "100%" }}>
                    <Typography variant="body2">{valueOrEmpty(params.row.ad_cost, formatToCurrency)}</Typography>
                </Stack>
            ),
        },
        {
            field: "ad_cpc",
            headerName: "Paid CPC (€)",
            flex: 1,
            minWidth: 75,
            type: "number",
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" alignItems="flex-start" justifyContent="left" spacing={0.5} py={1} sx={{ width: "100%" }}>
                    <Typography variant="body2">{valueOrEmpty(params.row.ad_cpc, formatToCurrency)}</Typography>
                </Stack>
            ),
        },
        {
            field: "ad_conversions",
            headerName: "Ad Conv.",
            flex: 1,
            minWidth: 100,
            type: "number",
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" alignItems="flex-start" justifyContent="left" spacing={0.5} py={1} sx={{ width: "100%" }}>
                    <Typography variant="body2">{valueOrEmpty(params.row.ad_conversions, fNumber)}</Typography>
                </Stack>
            ),
        },
        {
            field: "ROAS",
            headerName: "Paid ROAS",
            flex: 1,
            minWidth: 120,
            type: "number",
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" alignItems="flex-start" justifyContent="left" spacing={0.5} py={1} sx={{ width: "100%" }}>
                    <Typography variant="body2">{valueOrEmpty(params.row.paid_roas, formatPercent)}</Typography>
                </Stack>
            ),
        },
        {
            field: "opportunity",
            headerName: "Opportunity",
            flex: 1,
            minWidth: 120,
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    sx={{
                        backgroundColor: opportunityColors[params.row.opportunity.toLowerCase() as keyof typeof opportunityColors]?.backgroundColor,
                        color: opportunityColors[params.row.opportunity.toLowerCase() as keyof typeof opportunityColors]?.color,
                    }}
                    label={params.row.opportunity}
                    size="small"
                />
            ),
        },
    ] as GridColDef[];
};


type FilterOperator = 'lt' | 'lte' | 'gt' | 'gte' | 'equal' | 'notEqual' | 'between' | 'contains' | 'doesNotContain' | 'startsWith' | 'endsWith' | 'isEmpty' | 'isNotEmpty' | 'isAnyOf';

interface ColumnFilter {
    id: string; // Unique ID for each filter
    field: string;
    operator: FilterOperator;
    value: string;
    value2?: string; // For 'between' operator
    values?: string[]; // For 'isAnyOf' operator
}

export default function SearchTermsGrid() {
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        pageSize: 10,
        page: 0,
    });
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const { searchTermsGrid, isLoading, isError, isFetching } = useSearchTermsGridData(Object.assign({}, { ...filterModel, items: filterModel.items.filter((item) => !!item.value) }, paginationModel));
    const [topCompetField, setTopCompetField] = useState<string>("top5_compet_total");
    const columnVisibilityPopover = usePopover();
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const columns = useColumns();
    const apiRef = useRef<any>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    // Initialize column visibility model - all columns visible by default (empty object means all visible)
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

    const searchTermGridData = useMemo(() => {
        return searchTermsGrid?.data?.map((item: any) => ({
            ...item,
            top_compet__process: item[topCompetField] || [],
        }));
    }, [searchTermsGrid, topCompetField]);

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        setPaginationModel(model);
    };

    const handleTopCompetFieldChange = (field: string) => {
        if (field === topCompetField) {
            setTopCompetField("top5_compet_total");
            return;
        }
        setTopCompetField(field);
    };

    const columnGroupingModel: GridColumnGroupingModel = [
        {
            groupId: "actions",
            children: [{ field: "top_comp" }],
            renderHeaderGroup() {
                return (
                    <Stack direction="row" alignItems="center" justifyContent="space-around" spacing={0}>
                        <CategoryButton onClick={() => handleTopCompetFieldChange("top5_search_rate")} active={topCompetField === "top5_search_rate"}>
                            <Iconify icon="eva:search-fill" width={18} />
                        </CategoryButton>
                        <CategoryButton onClick={() => handleTopCompetFieldChange("top5_organic_rate")} active={topCompetField === "top5_organic_rate"}>
                            <SvgIcon component={TextIcon} />
                        </CategoryButton>
                        <CategoryButton onClick={() => handleTopCompetFieldChange("top5_shopping_rate")} active={topCompetField === "top5_shopping_rate"}>
                            <SvgIcon component={ShoppingCartIcon} />
                        </CategoryButton>
                    </Stack>
                );
            },
        }
    ]



    const Toolbar = (props: any) => {
        return (
            <Stack ref={toolbarRef} direction="row" alignItems="center" spacing={0} px={2}>
                <Typography variant="h5" sx={{ fontWeight: 600 }} color="text.primary">
                    Search Terms
                </Typography>
                <Box flexGrow={1} />
                <GridToolbar {...props} />
                <Button startIcon={
                    <Iconify icon="lets-icons:export" />
                } sx={{ height: 24 }} color="inherit">
                    Export
                </Button>
            </Stack>
        )
    }


    return (
        <Card>
            <CardContent sx={{
                px: 0.1,
                py: 0,
                overflow: "visible",
                minHeight: isLoading || isFetching ? 600 : "auto",
            }}>
                <DataGridPro
                    classes={{
                        root: "search-term-grid",
                        columnHeaders: "search-term-grid-column-headers",
                        columnHeader: "search-term-grid-column-header",
                    }}
                    apiRef={apiRef}
                    disableColumnResize={true}
                    pinnedColumns={{ left: ['search_term'], right: ['opportunity'] }}
                    loading={isLoading || isFetching}
                    rows={searchTermGridData ?? []}
                    columns={columns}
                    autoHeight
                    getRowHeight={() => "auto"}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={setColumnVisibilityModel}
                    filterModel={filterModel}
                    filterMode="server"
                    onFilterModelChange={setFilterModel}
                    disableDensitySelector
                    checkboxSelection={false}
                    disableRowSelectionOnClick
                    pagination
                    paginationMode="server"
                    paginationModel={paginationModel}
                    columnGroupingModel={columnGroupingModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    rowCount={searchTermsGrid?.total_rows || 0}
                    pageSizeOptions={[10, 20, 50, 100]}
                    slots={{
                        panel(props) {
                            const apiRefContext = useGridApiContext();
                            const [isPlaced, setIsPlaced] = useState(false);

                            const handleClickAway = useCallback(() => {
                                apiRefContext.current.hidePreferences();
                            }, [apiRefContext]);

                            const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
                                if (event.key === 'Escape') {
                                    apiRefContext.current.hidePreferences();
                                }
                            }, [apiRefContext]);

                            const modifiers = useMemo(() => [{
                                name: 'flip',
                                enabled: true,
                                options: {
                                    rootBoundary: 'document'
                                }
                            }, {
                                name: 'isPlaced',
                                enabled: true,
                                phase: 'main' as const,
                                fn: () => {
                                    setIsPlaced(true);
                                },
                                effect: () => () => {
                                    setIsPlaced(false);
                                }
                            }] as any, []);

                            const open = apiRef.current?.state?.preferencePanel?.open;
                            const anchorEl = toolbarRef.current;

                            if (!anchorEl) {
                                return null;
                            }

                            const panelProps = {
                                ...props,
                                as: undefined,
                                anchorEl: anchorEl,
                                open: open,
                            };

                            return (
                                <GridPanelRoot
                                    {...panelProps}
                                    placement="bottom-end"
                                    className={props.className}
                                    modifiers={modifiers}
                                >
                                    <ClickAwayListener
                                        mouseEvent="onPointerUp"
                                        touchEvent={false}
                                        onClickAway={handleClickAway}
                                    >
                                        <GridPaperRoot
                                            elevation={8}
                                            onKeyDown={handleKeyDown}
                                        >
                                            {isPlaced && props.children}
                                        </GridPaperRoot>
                                    </ClickAwayListener>
                                </GridPanelRoot>
                            );
                        },
                        toolbar: Toolbar,
                        filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                    }}
                    slotProps={{
                        toolbar: {
                            printOptions: {
                                disableToolbarButton: true,
                            },
                            csvOptions: {
                                disableToolbarButton: true,
                            },
                        },
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
                        },
                        loadingOverlay: {
                            variant: 'skeleton',
                            noRowsVariant: 'skeleton',
                        },
                    }}
                />
            </CardContent>
        </Card>
    );
}